import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LOCATIONS } from '@/data/gameData';
import { ZoomIn, ZoomOut, MapPin } from 'lucide-react';

interface WorldMapProps {
  currentLocation: string;
  onLocationSelect: (location: string) => void;
  onClose: () => void;
}

export const WorldMap: React.FC<WorldMapProps> = ({ currentLocation, onLocationSelect, onClose }) => {
  const [zoom, setZoom] = useState(1);

  // Map layout positions for each location (relative coordinates)
  const locationPositions: Record<string, { x: number; y: number }> = {
    'The Red Market': { x: 40, y: 30 },
    'The Lazerate': { x: 35, y: 50 },
    'Vestuvia': { x: 60, y: 45 },
    'The Oasis': { x: 20, y: 70 },
    'The Single Cell Biom': { x: 70, y: 25 },
    'The Badlands': { x: 50, y: 75 },
    'The Mushroom Caves': { x: 15, y: 40 },
    'The Bioluminescent Caves': { x: 25, y: 60 },
    'The Split Mountains': { x: 75, y: 60 },
    'The Ecological Dead Zone': { x: 80, y: 80 },
  };

  const locationConnections = [
    ['The Red Market', 'The Lazerate'],
    ['The Red Market', 'Vestuvia'],
    ['The Lazerate', 'The Mushroom Caves'],
    ['The Mushroom Caves', 'The Oasis'],
    ['The Oasis', 'The Badlands'],
    ['Vestuvia', 'The Single Cell Biom'],
    ['The Single Cell Biom', 'The Split Mountains'],
    ['The Split Mountains', 'The Ecological Dead Zone'],
    ['The Bioluminescent Caves', 'The Lazerate'],
  ];

  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl bg-gradient-to-br from-slate-900 to-black border-orange-500 p-6 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center border-b border-orange-500 pb-4">
          <div>
            <h2 className="text-3xl font-bold text-orange-500">World Map</h2>
            <p className="text-gray-400 text-sm">Dark Hells - Explore the 10 Realms</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setZoom(Math.min(zoom + 0.2, 2))}
              size="sm"
              className="bg-slate-700 hover:bg-orange-600"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => setZoom(Math.max(zoom - 0.2, 0.6))}
              size="sm"
              className="bg-slate-700 hover:bg-orange-600"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Map Canvas */}
        <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-gray-700 h-96 overflow-hidden">
          <svg
            className="w-full h-full absolute inset-0"
            style={{ transform: `scale(${zoom})`, transformOrigin: '50% 50%' }}
          >
            {/* Grid background */}
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(100,100,100,0.1)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />

            {/* Connection lines */}
            {locationConnections.map((connection, idx) => {
              const pos1 = locationPositions[connection[0]];
              const pos2 = locationPositions[connection[1]];
              if (!pos1 || !pos2) return null;

              return (
                <line
                  key={idx}
                  x1={`${pos1.x}%`}
                  y1={`${pos1.y}%`}
                  x2={`${pos2.x}%`}
                  y2={`${pos2.y}%`}
                  stroke="rgba(249, 115, 22, 0.3)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
              );
            })}

            {/* Location markers */}
            {LOCATIONS.map((location) => {
              const pos = locationPositions[location];
              if (!pos) return null;
              const isCurrent = currentLocation === location;

              return (
                <g key={location}>
                  <circle
                    cx={`${pos.x}%`}
                    cy={`${pos.y}%`}
                    r={isCurrent ? '8' : '6'}
                    fill={isCurrent ? '#f97316' : '#1e293b'}
                    stroke={isCurrent ? '#fbbf24' : '#f97316'}
                    strokeWidth={isCurrent ? '3' : '2'}
                  />
                  {isCurrent && (
                    <circle
                      cx={`${pos.x}%`}
                      cy={`${pos.y}%`}
                      r="12"
                      fill="none"
                      stroke="#fbbf24"
                      strokeWidth="1"
                      opacity="0.5"
                    >
                      <animate attributeName="r" from="12" to="16" dur="1.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-slate-800/90 rounded p-3 border border-gray-600 text-xs space-y-1">
            <div className="flex items-center gap-2 text-gray-300">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span>Location</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <div className="w-3 h-3 bg-orange-500 rounded-full border-2 border-yellow-400"></div>
              <span>Current Location</span>
            </div>
          </div>
        </div>

        {/* Location List */}
        <div className="space-y-3">
          <p className="text-orange-400 font-semibold text-sm">Destinations:</p>
          <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
            {LOCATIONS.map((location) => (
              <Button
                key={location}
                onClick={() => {
                  onLocationSelect(location);
                  onClose();
                }}
                variant={currentLocation === location ? 'default' : 'outline'}
                className={`justify-start text-xs h-auto py-2 ${
                  currentLocation === location
                    ? 'bg-orange-600 border-orange-600'
                    : 'border-gray-600 text-gray-300 hover:text-white'
                }`}
              >
                <MapPin className="w-3 h-3 mr-1" />
                {location}
              </Button>
            ))}
          </div>
        </div>

        {/* Close Button */}
        <Button
          onClick={onClose}
          className="w-full bg-gray-700 hover:bg-gray-600 text-white"
        >
          Close Map
        </Button>
      </Card>
    </div>
  );
};
